import { Routes, Route, useLocation, useNavigate, Navigate } from "react-router-dom";
import { lazy, Suspense, useEffect, useState } from "react";
import { clearAuth, isAuthTokenPresent } from "./utils/apiClient.js";
import ErrorBoundary from "./components/ErrorBoundary.js";

const Main = lazy(() => import("./views/Main.js"));
const Login = lazy(() => import("./views/Login.js"));
const Logout = lazy(() => import("./views/Logout.js"));
const ViewMessage = lazy(() => import("./views/ViewMessage.js"));

function usePageViews() {
  const location = useLocation();
  useEffect(() => {
    if (process.env.NODE_ENV !== "production") {
      console.log(location.pathname);
    }
  }, [location]);
}

async function verifyToken() {
  if (!isAuthTokenPresent()) return false;
  try {
    const response = await fetch(`${process.env.REACT_APP_API}/auth`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: localStorage.getItem("authJWT"),
      },
    });
    if (response.status === 401) {
      clearAuth();
      return false;
    }
    return response.ok;
  } catch {
    // Network failure: assume still authed so cached UI keeps working on flaky wifi.
    return true;
  }
}

function RequireAuth({ children }) {
  const [status, setStatus] = useState("checking");
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const ok = await verifyToken();
      if (cancelled) return;
      if (ok) {
        setStatus("authed");
      } else {
        clearAuth();
        navigate("/login", { replace: true });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  if (status === "checking") {
    return (
      <div className="fetch-failed">
        <div className="loader">
          <div className="loader-spinner"></div>
        </div>
        <h2>Lade...</h2>
      </div>
    );
  }
  return children;
}

function AppFallback() {
  return (
    <div className="fetch-failed">
      <div className="loader">
        <div className="loader-spinner"></div>
      </div>
      <h2>Lade...</h2>
    </div>
  );
}

function App() {
  usePageViews();

  return (
    <ErrorBoundary>
      <div className="app container">
        <div className="content">
          <Suspense fallback={<AppFallback />}>
            <Routes>
              <Route
                path="/"
                element={
                  <RequireAuth>
                    <Main />
                  </RequireAuth>
                }
              />
              <Route
                path="/msg/:msg"
                element={
                  <RequireAuth>
                    <ViewMessage />
                  </RequireAuth>
                }
              />
              <Route path="/login" element={<Login />} />
              <Route path="/logout" element={<Logout />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </div>
      </div>
    </ErrorBoundary>
  );
}

export default App;
